import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ChevronDown, ChevronUp, Calculator, Info } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Calculate the monthly payment factor per $1,000 borrowed
 * Formula: factor = (r × (1+r)^n) / ((1+r)^n - 1) × 1000
 */
const calculateFactor = (annualRate: number, termYears: number): number => {
  const r = annualRate / 100 / 12;
  const n = termYears * 12;
  if (r === 0) return 1000 / n;
  return (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1) * 1000;
};

const RATES = [2.0, 2.5, 3.0, 3.5, 4.0, 4.5, 5.0, 5.5, 6.0, 6.5, 7.0, 7.5, 8.0, 8.5, 9.0];
const HIGHLIGHT_RATE = 6.0;

// Example from the reference image
const EXAMPLE = {
  loanAmount: 197500,
  rate: 6.0,
  term: 15,
  factor: calculateFactor(6.0, 15),
  monthlyTaxes: 250,
  monthlyInsurance: 125,
};

const examplePI = (EXAMPLE.loanAmount / 1000) * EXAMPLE.factor;
const examplePITI = examplePI + EXAMPLE.monthlyTaxes + EXAMPLE.monthlyInsurance;

interface FactorPaymentsChartProps {
  defaultExpanded?: boolean;
}

export const FactorPaymentsChart: React.FC<FactorPaymentsChartProps> = ({ defaultExpanded = true }) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <Card className="border-border/40 bg-card/80 backdrop-blur-sm">
      <CardHeader
        className="cursor-pointer select-none py-4"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <CardTitle className="flex items-center gap-2 text-base md:text-lg">
          <Calculator className="w-5 h-5 text-primary" />
          Factor Payments Chart — Monthly P&I per $1,000 Borrowed
          <span className="ml-auto">
            {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
          </span>
        </CardTitle>
      </CardHeader>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <CardContent className="pt-0 space-y-6">
              {/* Factor Table */}
              <div className="rounded-lg border border-border/60 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="font-semibold text-foreground">Interest Rate</TableHead>
                      <TableHead className="font-semibold text-foreground text-right">15-Year Factor</TableHead>
                      <TableHead className="font-semibold text-foreground text-right">30-Year Factor</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {RATES.map((rate) => {
                      const isHighlighted = rate === HIGHLIGHT_RATE;
                      return (
                        <TableRow
                          key={rate}
                          className={isHighlighted ? 'bg-primary/10 font-semibold' : ''}
                        >
                          <TableCell className={isHighlighted ? 'text-primary font-semibold' : ''}>
                            {rate.toFixed(1)}%
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {calculateFactor(rate, 15).toFixed(2)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {calculateFactor(rate, 30).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* How to Use */}
              <div className="rounded-lg border border-border/60 bg-muted/30 p-4 space-y-2">
                <h3 className="text-sm font-semibold flex items-center gap-1.5 text-foreground">
                  <Info className="w-4 h-4 text-primary" />
                  How to Use This Chart
                </h3>
                <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                  <li>Divide your <strong className="text-foreground">loan amount</strong> by 1,000</li>
                  <li>Find your <strong className="text-foreground">interest rate</strong> and <strong className="text-foreground">loan term</strong> in the table</li>
                  <li>Multiply the result from Step 1 by the factor to get your monthly <strong className="text-foreground">Principal & Interest (P&I)</strong> payment</li>
                </ol>
              </div>

              {/* Live Example */}
              <div className="rounded-lg border border-border/60 bg-muted/30 p-4 space-y-3">
                <h3 className="text-sm font-semibold text-foreground">Example: PITI Breakdown</h3>
                <div className="text-sm text-muted-foreground space-y-1">
                  <p>
                    Loan Amount: <strong className="text-foreground">${EXAMPLE.loanAmount.toLocaleString()}</strong> · Rate: <strong className="text-foreground">{EXAMPLE.rate}%</strong> · Term: <strong className="text-foreground">{EXAMPLE.term} years</strong>
                  </p>
                  <p>
                    ${EXAMPLE.loanAmount.toLocaleString()} ÷ 1,000 = <strong className="text-foreground">{(EXAMPLE.loanAmount / 1000).toFixed(1)}</strong> × <strong className="text-foreground">{EXAMPLE.factor.toFixed(2)}</strong> = <strong className="text-primary">${examplePI.toFixed(2)}</strong> P&I/month
                  </p>
                </div>

                <div className="rounded-md border border-border/40 overflow-hidden">
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell className="text-muted-foreground text-sm">Loan Payment (P&I)</TableCell>
                        <TableCell className="text-right tabular-nums font-medium">${examplePI.toFixed(2)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="text-muted-foreground text-sm">Monthly Taxes (T)</TableCell>
                        <TableCell className="text-right tabular-nums font-medium">${EXAMPLE.monthlyTaxes.toFixed(2)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="text-muted-foreground text-sm">Monthly Insurance (I)</TableCell>
                        <TableCell className="text-right tabular-nums font-medium">${EXAMPLE.monthlyInsurance.toFixed(2)}</TableCell>
                      </TableRow>
                      <TableRow className="bg-primary/10 font-semibold">
                        <TableCell className="text-foreground text-sm font-semibold">Total PITI</TableCell>
                        <TableCell className="text-right tabular-nums font-bold text-primary">${examplePITI.toFixed(2)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </div>
            </CardContent>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
};
